import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import map from 'lodash/map';
import get from 'lodash/get';
import find from 'lodash/find';

import themeable from 'zignal-landscape/lib/themeable';
import Button from 'zignal-landscape/lib/Button';
import Col from 'zignal-landscape/lib/Col';
import DateRangePicker from 'zignal-landscape/lib/DateRangePicker';
import ExpansionPanelBody from 'zignal-landscape/lib/ExpansionPanelBody';
import Field from 'zignal-landscape/lib/Field';
import GridContent from 'zignal-landscape/lib/GridContent';
import Input from 'zignal-landscape/lib/Input';
import Label from 'zignal-landscape/lib/Label';
import Radio from 'zignal-landscape/lib/Radio';
import Row from 'zignal-landscape/lib/Row';
import Select from 'zignal-landscape/lib/Select';
import ChevronUpIcon from 'zignal-landscape/lib/Icon/ChevronUpIcon';
import DropdownIcon from 'zignal-landscape/lib/Icon/DropdownIcon';
import FilterIcon from 'zignal-landscape/lib/Icon/FilterIcon';
import SearchIcon from 'zignal-landscape/lib/Icon/SearchIcon';

import SmartFilterSummary from '../../components/SmartFilterSummary';
import { convertUrlParamTimesToDatePickerTimes } from 'helpers/timeFormat';

import * as actions from './SearchFilters.ducks';

const mapStateToProps = (
  (state) => ({
    isLoading: state.BotIntelligence.Page.isLoading,
    smartFilterSummaryOnSearch: state.BotIntelligence.Page.smartFilterSummaryOnSearch,
    endDate: state.BotIntelligence.SearchFilters.endDate,
    isOpen: state.BotIntelligence.SearchFilters.isOpen,
    profileIds: state.BotIntelligence.SearchFilters.profileIds,
    query: state.BotIntelligence.SearchFilters.query,
    searchBy: state.BotIntelligence.SearchFilters.searchBy,
    startDate: state.BotIntelligence.SearchFilters.startDate,
    selectedSmartFilterId: state.BotIntelligence.SearchFilters.selectedSmartFilterId,
    selectedSmartFilterSummary: state.BotIntelligence.SearchFilters.selectedSmartFilterSummary,
    timezone: state.AppUser.user.timezone
  })
);

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

class SearchFilters extends React.Component {
  constructor(props) {
    super(props);
    this.handleKeyPress = this.handleKeyPress.bind(this);
  }

  componentDidMount() {
    const { queryParams, setQueryParams, submitQuery, timezone } = this.props;
    const { q: query, start, end } = queryParams;
    // set initial query params if query is present and initiate search
    if (query){
      const times = convertUrlParamTimesToDatePickerTimes(start, end, timezone);
      setQueryParams({ query, ...times });
      submitQuery();
    }
  }

  componentWillUnmount() {
    this.props.clearSearchForm();
  }

  handleKeyPress({key}) {
    if (key === 'Enter') { this.props.submitQuery(); }
  }

  render() {
    const {
      styles,
      endDate,
      isLoading,
      isOpen,
      query,
      searchBy,
      profileIds,
      startDate,
      selectedSmartFilterId,
      selectedSmartFilterSummary,
      smartFilterSummaryOnSearch,
      timezone,
      // passed by parent
      profileOptions,
      submitQuery,
      smartFilters,
      campaignId,
      // actions
      editQuery,
      selectProfiles,
      selectSmartFilterId,
      selectSearchByOption,
      toggleSearchSectionVisibility,
      fetchSmartFilter,
      pickDateRange
    } = this.props;

    let searchTermsInput;
    if(searchBy === 'keywords') {
      searchTermsInput = (<Input
        data-cy="search-input-influence-intelligence"
        placeholder="Search for..."
        suffixIcon={<SearchIcon />}
        value={query}
        onChange={editQuery}
        onKeyPress={this.handleKeyPress}
      />);
    } else {
      const selectValue = map(profileIds, (id) => find(profileOptions, {value: id}));

      searchTermsInput = (<Select
        placeholder={profileOptions.length ? 'Select...' : 'No datasets available...'}
        value={selectValue}
        options={profileOptions}
        onChange={(selectedOptions) => {
          selectProfiles(map(selectedOptions, 'value'));
        }}
        isMulti
        isClearable
      />);
    }

    const isFasKeywordsEnabled = window.splitHelper?.getTreatment?.('ZII_FAS_Access') === 'on';

    // All 3 fields shown to user must NOT be empty
    const isValidSearch = (startDate !== '' && endDate !== '') &&
      ((searchBy === 'keywords' && query.length > 0) ||
      (searchBy === 'profiles' && profileIds.length > 0));

    return (
      <div className={styles.SearchFilters}>
        <aside className={styles.visibilityButton}>
          <div className={styles.iconPosition} onClick={toggleSearchSectionVisibility}>
            {isOpen ? <ChevronUpIcon /> : <DropdownIcon />}
          </div>
        </aside>

        <h4>Search</h4>
        <ExpansionPanelBody isOpen={isOpen} className={styles.expansionPanelBody}>
          <Row>
            <Col md={3}>
              <GridContent className={styles.firstInputContainer}>
                <Field>
                  {isFasKeywordsEnabled ? (
                    <div className={styles.searchForSection}>
                      <Label>
                        <Radio
                          id="keywords"
                          name="searchBy"
                          value="keywords"
                          onChange={selectSearchByOption}
                          checked={searchBy === 'keywords'}
                        />
                        <span className={styles.normalText} htmlFor="keywords">Keywords</span>
                      </Label>
                      <Radio
                        id="profiles"
                        name="searchBy"
                        value="profiles"
                        onChange={selectSearchByOption}
                        checked={searchBy === 'profiles'}
                      />
                      <Label htmlFor="profiles" inline>Datasets</Label>
                    </div>
                  ) : (
                    <div className={styles.smartFilterSection}>
                      <Label>Datasets</Label>
                    </div>
                  )}

                  {searchTermsInput}
                </Field>
              </GridContent>
            </Col>

            {(searchBy === 'profiles') &&
              <Col md={3}>
                <GridContent>
                  <Field>
                    <div className={styles.smartFilterSection}>
                      <Label>
                        <Label htmlFor="smartFilter" inline>
                          <FilterIcon />
                          <span className={styles.smartFilterLabel}>Smart Filter</span>
                        </Label>
                      </Label>
                    </div>
                    <Select
                      id="smartFilter"
                      placeholder={smartFilters.length ? 'Select...' : 'No smart filter available...'}
                      value={find(smartFilters, {value : selectedSmartFilterId})}
                      options={smartFilters}
                      onChange={(smartFilter) => {
                        const smartFilterId = get(smartFilter, 'value', '');
                        selectSmartFilterId(smartFilterId);
                        if (smartFilterId) {
                          fetchSmartFilter({
                            profileId: campaignId,
                            filterId: smartFilterId
                          });
                        }
                      }}
                      selectStyles={{ singleValue: base => ({ ...base, padding: 0, lineHeight: 'inherit', color: 'rgb(180, 191, 214)' }) }}
                      isClearable
                    />
                  </Field>
                </GridContent>
              </Col>
            }

            <Col md={5}>
              <GridContent>
                <Field>
                  <div className={styles.dateLabelsContainer}>
                    <Label>Date Range</Label>
                    <div className={styles.endDateLabel}>
                      <Label />
                    </div>
                  </div>
                  <DateRangePicker
                    startEndDates={{
                      startDate,
                      endDate
                    }}
                    onChange={pickDateRange}
                    inputDisplayFormat={'MM/DD/YYYY h:mma'}
                    timezone={timezone}
                  />
                </Field>
              </GridContent>
            </Col>

            <Col className={styles.searchButtonSection} md={1}>
              <GridContent>
                <Button
                  buttonStyle="secondary"
                  onClick={submitQuery}
                  disabled={!isValidSearch || isLoading}
                >
                  Search
                </Button>
              </GridContent>
            </Col>
          </Row>
          {(searchBy === 'profiles')
            && selectedSmartFilterId
            && selectedSmartFilterSummary
            && (!smartFilterSummaryOnSearch || selectedSmartFilterSummary.name !== smartFilterSummaryOnSearch.name)
            && <Row>
              <Col md={12}>
                <SmartFilterSummary
                  smartFilter={selectedSmartFilterSummary}
                  isExpandedVersion
                />
              </Col>
            </Row>
          }
        </ExpansionPanelBody>

        {smartFilterSummaryOnSearch &&
          <div className={styles.lastSmartFilterContainer}>
            <Col md={12}>
              <SmartFilterSummary
                smartFilter={smartFilterSummaryOnSearch}
              />
            </Col>
          </div>
        }
      </div>
    );
  }
}

SearchFilters.propTypes = {
  styles: PropTypes.object.isRequired,
  endDate: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  profileIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedSmartFilterId: PropTypes.string.isRequired,
  selectedSmartFilterSummary: PropTypes.object,
  smartFilterSummaryOnSearch: PropTypes.object,
  query: PropTypes.string.isRequired,
  searchBy: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  // passed by parent
  profileOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })).isRequired,
  smartFilters: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })).isRequired,
  submitQuery: PropTypes.func.isRequired,
  campaignId: PropTypes.string,
  queryParams: PropTypes.object,
  // actions
  editQuery: PropTypes.func.isRequired,
  pickDateRange: PropTypes.func.isRequired,
  selectProfiles: PropTypes.func.isRequired,
  selectSmartFilterId: PropTypes.func.isRequired,
  selectSearchByOption: PropTypes.func.isRequired,
  toggleSearchSectionVisibility: PropTypes.func.isRequired,
  clearSearchForm: PropTypes.func.isRequired,
  setQueryParams: PropTypes.func.isRequired,
  fetchSmartFilter: PropTypes.func.isRequired
};

export default connect(mapStateToProps, actions, mergeProps)(themeable(SearchFilters));
